import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  ImageOff,
  Loader2,
  RefreshCw,
  Store,
  Users,
  XCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { adminApi } from "@/lib/admin-api";

function fmt(n) {
  return typeof n === "number" ? n.toLocaleString() : "—";
}

function StatCard({ title, value, hint, icon: Icon, tone = "default" }) {
  const toneClass =
    tone === "warn"
      ? "text-amber-600 dark:text-amber-400"
      : tone === "danger"
        ? "text-red-600 dark:text-red-400"
        : "text-foreground";
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function HealthDot({ ok }) {
  return ok ? (
    <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-sm">
      <CheckCircle2 className="h-3.5 w-3.5" /> Healthy
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-red-600 dark:text-red-400 text-sm">
      <XCircle className="h-3.5 w-3.5" /> Down
    </span>
  );
}

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [s, h] = await Promise.all([adminApi.stats(), adminApi.health()]);
      setStats(s?.counts || null);
      setHealth(h || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading admin overview…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" /> Failed to load
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {error}
          <div className="mt-3">
            <Button size="sm" variant="outline" onClick={() => load()}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Operator overview</h1>
          <p className="text-sm text-muted-foreground">
            Snapshot of system state. Refreshed at{" "}
            {new Date().toLocaleTimeString()}.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load(false)}
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Stores" value={fmt(stats?.stores)} icon={Store} />
        <StatCard title="Users" value={fmt(stats?.users)} icon={Users} />
        <StatCard
          title="Competitors (watched)"
          value={fmt(stats?.competitors?.watched)}
          hint={`${fmt(stats?.competitors?.total)} total`}
          icon={Users}
        />
        <StatCard
          title="Ads (active)"
          value={fmt(stats?.ads?.active)}
          hint={`${fmt(stats?.ads?.total)} total · ${fmt(stats?.ads?.withCreative)} with creative`}
          icon={Database}
        />
        <StatCard
          title="Meta ads missing creative"
          value={fmt(stats?.ads?.metaMissingCreative)}
          hint="Targets for the Meta image backfill"
          icon={ImageOff}
          tone={(stats?.ads?.metaMissingCreative || 0) > 0 ? "warn" : "default"}
        />
        <StatCard
          title="Jobs (last 24h)"
          value={fmt(stats?.jobs?.last24h)}
          icon={Database}
        />
        <StatCard
          title="Failed jobs (last 7d)"
          value={fmt(stats?.jobs?.failedLast7d)}
          icon={AlertTriangle}
          tone={(stats?.jobs?.failedLast7d || 0) > 0 ? "danger" : "default"}
        />
        <StatCard
          title="First-winner emails sent"
          value={fmt(stats?.firstWinnerEmailsSent)}
          icon={CheckCircle2}
        />
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Database</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <HealthDot ok={health?.database?.ok} />
            </div>
            {health?.database?.latencyMs != null && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Ping</span>
                <span>{health.database.latencyMs} ms</span>
              </div>
            )}
            {health?.database?.error && (
              <p className="text-xs text-destructive">{health.database.error}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Snapshot scraper</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              <HealthDot ok={health?.scraper?.ok} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">URL</span>
              <code className="text-xs">{health?.scraper?.url}</code>
            </div>
            {health?.scraper?.error && (
              <p className="text-xs text-destructive">{health.scraper.error}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Environment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">NODE_ENV</span>
              <Badge variant="secondary">{health?.nodeEnv || "unknown"}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {Object.entries(health?.env || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5">
                  {v ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <XCircle className="h-3 w-3 text-red-600" />
                  )}
                  <span className={v ? "text-foreground" : "text-muted-foreground"}>
                    {k}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
